import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material'
import { blue, orange } from '@mui/material/colors'
import { OverlayState } from '@/hooks/useOverlay'
import {
  useCreateProjectFromTemplate,
  useGetProjectTemplates,
} from '@/service-library/hooks/project-templates'
import { useGetProject } from '@/service-library/hooks/projects'
import { ProjectTemplate } from '@/types/project-templates'
import { ProjectCategory } from '@/types/projects'
import { showErrorSnackbar } from '@/utils/snackbars'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import { Dialog, DialogContent, DialogFooter } from '../dialog'
import useSubscribe from '../notifications/useSubscribe'

function GradientCircularProgress() {
  return (
    <>
      <svg width={0} height={0}>
        <defs>
          <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={blue['A700']} />
            <stop offset="100%" stopColor={orange['A700']} />
          </linearGradient>
        </defs>
      </svg>
      <CircularProgress
        size={64}
        sx={{ 'svg circle': { stroke: 'url(#my_gradient)' } }}
      />
    </>
  )
}

function TemplateList({
  projectTemplates,
  handleCreateProject,
  title,
}: {
  projectTemplates: ProjectTemplate[]
  handleCreateProject: (template: ProjectTemplate) => void
  title: string
}) {
  if (projectTemplates.length === 0) return null
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary" sx={{ px: 3 }}>
        {title}
      </Typography>
      <List dense disablePadding>
        {projectTemplates.map((template) => {
          return (
            <ListItemButton
              key={template.id}
              onClick={() => handleCreateProject(template)}
              sx={{ px: 3 }}
            >
              {template.project_display_name}
            </ListItemButton>
          )
        })}
      </List>
    </Stack>
  )
}

type ProjectTemplateDialogProps = {
  overlay: OverlayState
  category: ProjectCategory
}

export default function ProjectTemplateDialog({
  overlay,
  category,
}: ProjectTemplateDialogProps) {
  const { rootOrganization } = useCurrentAccountContext()
  const { projectTemplates } = useGetProjectTemplates({
    filters: {
      limit: '1000',
    },
  })

  const [creating, setCreating] = useState(false)
  const [creatingProjectId, setCreatingProjectId] = useState<
    string | undefined
  >()

  // We attempt to fetch the new project as a backup to the notification, in
  // case we don't get the completion notification.
  const { project: newProject } = useGetProject({
    id: creatingProjectId as string,
    enabled: !!creatingProjectId,
    filters: {
      filters__only: 'id,setup_state',
    },
    refetchInterval: 1000 * 60 * 3, // Refetch every three minutes
  })

  const { createProjectFromTemplate } = useCreateProjectFromTemplate({
    onSuccess: ({ id }) => {
      setCreatingProjectId(id)
    },
    onError: () => {
      showErrorSnackbar('Failed to create project. Please try again later.')
      setCreating(false)
    },
  })
  const navigate = useNavigate()

  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      const data = JSON.parse(event.data)
      if (data.action.includes('create-from-template.complete')) {
        navigate(`/projects/${data.action_data.id}`)
      } else if (data.action.includes('create-from-template.failed')) {
        showErrorSnackbar(
          `Failed to create project from template: ${data.action_data.name}`,
        )
        setCreating(false)
        setCreatingProjectId(undefined)
      }
    },
    [navigate],
  )

  useSubscribe({
    onMessage: handleMessage,
    body: creatingProjectId
      ? [
          {
            endpoint_path: '/v2/pd/projects',
            obj_ids: [creatingProjectId],
          },
        ]
      : undefined,
  })

  function handleCreateProject(template: ProjectTemplate) {
    setCreating(true)
    createProjectFromTemplate({
      template_id: template.id,
      category_id: category.id,
      org_id: rootOrganization.id,
    })
  }

  // If we get the project and its state is "complete" then we missed the notification
  useEffect(() => {
    if (newProject?.setup_state === 'complete') {
      navigate(`/projects/${newProject.id}`)
    }
  }, [newProject, navigate])

  return (
    <Dialog
      {...overlay}
      title={creating ? undefined : 'Select a Project Template'}
      maxWidth="xs"
      disableEscapeKeyDown={creating}
      disableClickAway={creating}
    >
      {creating ? (
        <Stack
          spacing={4}
          sx={{ height: 300 }}
          alignItems="center"
          justifyContent="center"
        >
          <GradientCircularProgress />

          <Stack spacing={1} textAlign="center">
            <Typography variant="h6">Creating Project</Typography>
            <Typography variant="caption" color="text.secondary">
              This may take a few minutes.
            </Typography>
          </Stack>
        </Stack>
      ) : (
        <>
          <DialogContent disablePadding>
            <Stack spacing={2}>
              {projectTemplates.length === 0 && (
                <Typography variant="body1" sx={{ px: 3 }}>
                  No templates available.
                </Typography>
              )}

              <TemplateList
                projectTemplates={projectTemplates}
                handleCreateProject={handleCreateProject}
                title="Templates"
              />
            </Stack>
          </DialogContent>
          <DialogFooter>
            <Button variant="text" onClick={overlay.close}>
              Cancel
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  )
}
